/**
 * A document picked from the person's Vault (owner, 20 Oct 2026 — D1: the
 * Vault is one, every hero picks from it): which of their files can be
 * linked, and how they are laid out — the same on Citizen Web and the phone.
 *
 * A file can be picked when its bytes are in storage and it was not rejected
 * (the server's own check, `VaultFileLinkService`, decides again), when it is
 * of a kind the place accepts (a boarding pass reader reads images and PDFs),
 * and when the place does not hold it already. The newest first, grouped by
 * the hero that keeps it — the documents of a move (TelePortal) first, then
 * the trips (InOut), then the rest; files of no hero last. Files whose type
 * fits what the place asks for (D2 — `inoutItemOfDocumentType`, a task's own
 * type) come before all of them, as "Suggested".
 */

/** What a picker needs of a Vault file (`VaultFile`, `@tonnie/sdk`). */
export interface VaultPickableFile {
  id: string;
  fileName: string;
  /** The hero that keeps the file — a Vault category, or `other`. */
  hero: string;
  /** The Vault's lifecycle as served: `uploaded`, `validated`, `rejected`, `pending`. */
  status: string;
  contentType: string;
  uploadedAt: string;
  /** What the document is, when known (D2) — null when the Vault has not been told. */
  documentType: string | null;
}

/** The group of files that fit what the place asks for — before every hero's. */
export const VAULT_PICK_SUGGESTED = 'suggested';

/** The order heroes' files are offered in; a hero not named here comes before `other`. */
export const VAULT_PICK_HERO_ORDER = [
  'teleportal',
  'inout',
  'checkup',
  'qultureup',
  'care',
  'passport',
  'career',
] as const;

const OTHER = 'other';

/** Each hero's product name — never translated; a file of no hero has none (say "Other"). */
const VAULT_HERO_NAMES: Readonly<Record<string, string>> = {
  checkup: 'CheckUp',
  teleportal: 'TelePortal',
  qultureup: 'QultureUp',
  care: 'Care',
  passport: 'Passport',
  career: 'Career',
  inout: 'InOut',
};

/** The name of the hero that keeps a file; null for a file of no hero (the caller's "Other"). */
export function vaultHeroName(hero: string): string | null {
  return VAULT_HERO_NAMES[hero] ?? null;
}

/** In storage and not rejected — what can be linked. */
const PICKABLE_STATUSES: ReadonlySet<string> = new Set(['uploaded', 'validated']);

/** Whether a content type is one of `accept` — exact (`application/pdf`) or a family (`image/*`). */
export function vaultMimeAccepted(contentType: string, accept: readonly string[]): boolean {
  const type = contentType.toLowerCase();
  return accept.some((wanted) => {
    const w = wanted.toLowerCase();
    return w.endsWith('/*') ? type.startsWith(w.slice(0, -1)) : type === w;
  });
}

export function vaultPickableFiles<F extends VaultPickableFile>(
  files: readonly F[],
  options: {
    /** The content types the place accepts; any when not said. */
    accept?: readonly string[];
    /** Files the place holds already. */
    exclude?: Iterable<string>;
  } = {}
): F[] {
  const held = new Set(options.exclude ?? []);
  return files
    .filter(
      (file) =>
        PICKABLE_STATUSES.has(file.status) &&
        !held.has(file.id) &&
        (!options.accept || vaultMimeAccepted(file.contentType, options.accept))
    )
    .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : a.uploadedAt > b.uploadedAt ? -1 : 0));
}

function heroRank(hero: string): number {
  const at = (VAULT_PICK_HERO_ORDER as readonly string[]).indexOf(hero);
  if (at >= 0) return at;
  return hero === OTHER ? VAULT_PICK_HERO_ORDER.length + 1 : VAULT_PICK_HERO_ORDER.length;
}

/**
 * The files by the hero that keeps them, in `VAULT_PICK_HERO_ORDER`; each group
 * keeps its order. Those `suggested` — their type fits what the place asks
 * for — come first, as their own group (`VAULT_PICK_SUGGESTED`).
 */
export function vaultPickGroups<F extends VaultPickableFile>(
  files: readonly F[],
  suggested: (file: F) => boolean = () => false
): Array<{ hero: string; files: F[] }> {
  const fits = files.filter(suggested);
  const groups = new Map<string, F[]>();
  for (const file of files) {
    if (fits.includes(file)) continue;
    groups.set(file.hero, [...(groups.get(file.hero) ?? []), file]);
  }
  return [
    ...(fits.length > 0 ? [{ hero: VAULT_PICK_SUGGESTED, files: fits }] : []),
    ...[...groups.entries()]
      .sort(([a], [b]) => heroRank(a) - heroRank(b))
      .map(([hero, held]) => ({ hero, files: held })),
  ];
}
